import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  makeStyles,
  CardContent,
  CardActions
} from "@material-ui/core";
import { Checkbox, FormControlLabel, CardHeader, Button, InputLabel, Link } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField } from "formik-material-ui";
import { useNavigate } from "react-router-dom";
import "./AffiliateRegisterForm.scss";
import AffiliateService from "../../Services/http/AffiliateService";
import { setIsActiveSpinner, setuserRol } from "../../Redux/GeneralSlice";
import LoadingOverlay from "react-loading-overlay-ts";
import { SessionSorageInstance } from "../../Services/SessionStorageService";
import PhoneInputField from "../Helper/PhonePrefixForm/PhoneInputField";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import OnboardingLayout from "../Layout/OnboardingLayout";

const useStyle = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(3),
  },
  button: {
    margin: theme.spacing(1),
  },
}));

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  phoneNumber: "",
};

const AffiliateRegisterForm = () => {
  const classes = useStyle();
  const { t, i18n } = useTranslation("registerForm");
  const [isApproveTermMessage, setisApproveTermMessage] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [countryCode, setCountryCode] = useState('');
  const [isApproveTerm, setIsApproveTerm] = useState(false);
  const [errorMessage, seterrorMessage] = useState("");
  const [isShowErrorMessage, setisShowErrorMessage] = useState(false);
  const [contactHubspotId, setContactHubspotId] = useState(null);
  const [endInitialValues, setEndInitialValues] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isActive = useSelector((state: any) => state.general.isActiveSpinner);

  const digitsAndEnglishOnly = /^[a-zA-Z0-9]*$/;

  const validationSchema = useMemo(() =>
    Yup.object().shape({
      firstName: Yup.string()
        .matches(digitsAndEnglishOnly, t("errors.invalidPassword"))
        .min(2, t("errors.passwordLength"))
        .required(t("errors.required")),
      lastName: Yup.string()
        .matches(digitsAndEnglishOnly, t("errors.invalidPassword"))
        .min(2, t("errors.passwordLength"))
        .required(t("errors.required")),
      email: Yup.string()
        .email(t("errors.invalidEmail"))
        .required(t("errors.required")),
      password: Yup.string()
        .matches(digitsAndEnglishOnly, t("errors.invalidPassword"))
        .min(8, t("errors.passwordLength"))
        .max(32, t("errors.maxPasswordLength") || "Must contain up to 32 characters!")
        .required(t("errors.required")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], t("errors.passwordMismatch"))
        .required(t("errors.required")),
      phoneNumber: Yup.string()
        .required(t("errors.phoneRequired")),
    }), [t]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setContactHubspotId(params.get('contactHubspotId'));
    setEndInitialValues(true);
  }, []);

  const onSubmitRegister = async (values: any) => {
    if (!isApproveTerm) {
      setisApproveTermMessage(t("errors.approveTerms"));
    } else {
      setisApproveTermMessage("");

      values.countryCode = countryCode;
      values.contactHubspotId = contactHubspotId;

      dispatch(setIsActiveSpinner(true));
      const response = await AffiliateService.registerAffiliate(values);
      dispatch(setIsActiveSpinner(false));

      const message: string =
        getErrorMessage(
          JSON.parse(JSON.stringify(response))
            ? JSON.parse(JSON.stringify(response)).message
            : null
        ) || "";

      seterrorMessage(message.toString());
      if (message === "success") {
        SessionSorageInstance.setUserDetails(response);
        dispatch(setuserRol(SessionSorageInstance.getUserRole()));
        navigate("/affiliate-layout");
        setisShowErrorMessage(false);
      } else {
        setisShowErrorMessage(true);
      }
    }
  };




  const getErrorMessage = (responseMessage: string) => {
    switch (responseMessage) {
      case null: return t("errors.connection");
      case "success": return "success";
      case "email_exists": return t("errors.emailExists");
      default: return responseMessage;
    }
  };

  const onChangeIsApproveTerm = (value: any) => {
    setIsApproveTerm(value.target.checked);
  };

  return (
    <OnboardingLayout>
      <LoadingOverlay active={isActive} spinner className="register-align-center">
        <Grid className="register-grid" container justifyContent="center">
          <Grid item>
            <CardHeader title={t("title")} />
            <Typography variant="subtitle1" align="center" gutterBottom sx={{ fontWeight: 500, color: '#333' }}>
              {t("affiliatePortal")}
            </Typography>

            {endInitialValues &&
              <Formik
                key={i18n.language}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmitRegister}
                initialTouched={{ phoneNumber: true }}
                enableReinitialize={true}
              >
                {({ dirty, isValid, values }) => (
                  <Form className="form-width">
                    <CardContent>
                      <Grid container spacing={1} justifyContent="center">
                        <Grid item xs={12} sm={6} md={12}>
                          <InputLabel>{t("email")}*</InputLabel>
                          <Field
                            name="email"
                            component={TextField}
                            fullWidth
                            size="small"
                            required
                            value={values.email}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12}>
                          <InputLabel>{t("firstName")}*</InputLabel>
                          <Field
                            name="firstName"
                            component={TextField}
                            fullWidth
                            size="small"
                            required
                            value={values.firstName}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12}>
                          <InputLabel>{t("lastName")}*</InputLabel>
                          <Field
                            name="lastName"
                            component={TextField}
                            fullWidth
                            size="small"
                            required
                            value={values.lastName}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12}>
                          <InputLabel>{t("password")}*</InputLabel>
                          <Field
                            name="password"
                            component={TextField}
                            type="password"
                            fullWidth
                            size="small"
                            required
                            value={values.password}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12}>
                          <InputLabel>{t("confirmPassword")}*</InputLabel>
                          <Field
                            name="confirmPassword"
                            component={TextField}
                            type="password"
                            fullWidth
                            size="small"
                            required
                            value={values.confirmPassword}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12}>
                          <InputLabel>{t("phoneNumber")}*</InputLabel>
                          <Field
                            component={PhoneInputField}
                            name="phoneNumber"
                            value={values.phoneNumber}
                            setCountryCode={setCountryCode}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={12} className="approve-checkbox">
                          <FormControlLabel
                            control={<Checkbox />}
                            label=""
                            onChange={onChangeIsApproveTerm}
                          />
                          <span className="f-13">
                            {t("terms")}{" "}
                            <a
                              className="blue-color"
                              href={require("../../Assets/Terms_and_Conditions.pdf")}
                              target="_blank"
                              style={{ margin: "0 3px", color: "blue" }}
                            >
                              {t("termsLink")}
                            </a>{" "}
                            and{" "}
                            <a
                              className="blue-color"
                              href={require("../../Assets/privacy_policy.pdf")}
                              target="_blank"
                              style={{ margin: "0 3px", color: "blue" }}
                            >
                              {t("privacyLink")}
                            </a>
                          </span>
                        </Grid>

                        <Grid item xs={12} sm={6} md={12} justifyContent="center" container>
                          <div style={{ color: "red", marginTop: "10px" }}>
                            {isApproveTermMessage}
                          </div>
                          {isShowErrorMessage && (
                            <div className="error-message">{errorMessage}</div>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Button
                        disabled={!dirty || !isValid}
                        type="submit"
                        className="button-login h-40p"
                      >
                        {t("submit")}
                      </Button>
                    </CardActions>
                    <p className="text-center gray-two-color mt-0 mb-2">
                      {t("alreadyHaveAccount")}
                      <Link className="blue-color pl-3p" underline="hover" href="/login-affiliate">
                        {" "}{t("signin")}
                      </Link>
                    </p>
                  </Form>
                )}
              </Formik>}
          </Grid>
        </Grid>
      </LoadingOverlay>
    </OnboardingLayout>
  );

};

export default AffiliateRegisterForm;
